import type { CSSProperties } from 'react';
import type { CategoryNode } from '@sebastian/shared';

/**
 * Colour coding for the order screen.
 *
 * A category's colour is owned by its *root* — a sub-category is the same
 * thing as its parent as far as a waiter scanning the screen is concerned, so
 * "Mains > Grill" and "Mains > Pasta" share the Mains colour, and an item
 * card borrows the colour of the category it sits in. The point is to find a
 * dish by shape and colour instead of by reading every tile.
 *
 * Colours are assigned by the root category's position in the menu rather
 * than stored, so there is nothing for anyone to configure and nothing to
 * migrate. Appending a category at the end leaves every existing colour where
 * it was, which is what protects the staff's muscle memory; deliberately
 * reordering the menu does re-colour it.
 */

export interface CategoryColor {
  /** Opaque, >= 4.5:1 on white. Swatches, selected labels, hovered borders. */
  ink: string;
  /**
   * The resting border on an item card: the same hue, held just off full
   * strength so the card still reads as white card + coloured edge rather
   * than as a block of colour.
   */
  border: string;
  /** Wash behind a selected category row. */
  tint: string;
}

/**
 * Ten hues, every one of them >= 4.5:1 against white (the floor the rest of
 * the design tokens hold to — these are read at arm's length on a tablet,
 * under whatever lighting the restaurant has).
 *
 * The order is deliberate: neighbours in this list are neighbours in the
 * category rail, so the list is arranged to put at least 96 degrees of hue
 * between consecutive entries. Sorting them into a rainbow would put two
 * near-identical oranges next to each other.
 */
const PALETTE_INK = [
  '#2563a8', // blue
  '#b4420a', // orange
  '#1e7d52', // green
  '#b32b5e', // rose
  '#5b7a12', // olive
  '#4a45c4', // indigo
  '#c0362c', // red
  '#0f766e', // teal
  '#7e3bbd', // purple
  '#8a5a0f', // amber
] as const;

function withAlpha(hex: string, percent: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  return `rgb(${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255} / ${percent}%)`;
}

export const CATEGORY_PALETTE: readonly CategoryColor[] = PALETTE_INK.map((ink) => ({
  ink,
  border: withAlpha(ink, 70),
  tint: withAlpha(ink, 10),
}));

/**
 * What an item in no known category gets. Items stay visible when their
 * category is hidden or has not loaded, they just fall back to the neutral
 * hairline the rest of the app uses.
 */
export const NEUTRAL_CATEGORY_COLOR: CategoryColor = {
  ink: 'var(--color-accent)',
  border: 'var(--color-line)',
  tint: 'transparent',
};

export type CategoryColorMap = ReadonlyMap<string, CategoryColor>;

function paint(node: CategoryNode, color: CategoryColor, into: Map<string, CategoryColor>): void {
  into.set(node.id, color);
  for (const child of node.children) paint(child, color, into);
}

/** Map every category id in the tree — at any depth — to its root's colour. */
export function buildCategoryColors(roots: CategoryNode[]): CategoryColorMap {
  const map = new Map<string, CategoryColor>();
  roots.forEach((root, index) => {
    paint(root, CATEGORY_PALETTE[index % CATEGORY_PALETTE.length]!, map);
  });
  return map;
}

export function colorFor(colors: CategoryColorMap, categoryId: string | null): CategoryColor {
  return (categoryId ? colors.get(categoryId) : undefined) ?? NEUTRAL_CATEGORY_COLOR;
}

/**
 * The two custom properties the `.cat-card` / `.cat-chip` rules in index.css
 * read. Inline rather than Tailwind arbitrary values because the hover state
 * needs the same pair, and a utility class cannot carry a per-item colour.
 */
export function categoryColorVars(color: CategoryColor): CSSProperties {
  return {
    '--cat-ink': color.ink,
    '--cat-border': color.border,
    '--cat-tint': color.tint,
  } as CSSProperties;
}
